import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout';

/**
 * WelcomePage - Displayed to new users after OAuth signup
 * Introduces the platform and guides users through getting started
 */
export const WelcomePage: FC = () => {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'there';

  const steps = [
    {
      number: 1,
      title: 'Browse Services',
      description:
        'Explore our range of massage therapies to find the perfect treatment for your needs.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      ),
    },
    {
      number: 2,
      title: 'Choose Your Therapist',
      description:
        'Select from our team of certified professionals based on their specializations and availability.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      number: 3,
      title: 'Book Your Session',
      description: 'Pick a convenient time slot and complete your booking in just a few clicks.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  ];

  return (
    <Layout>
      <Helmet>
        <title>Welcome to Massatherapy</title>
        <meta
          name="description"
          content="Welcome to Massatherapy. Get started with your first massage therapy booking."
        />
      </Helmet>

      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Welcome Header - Glassmorphism Card */}
          <div className="backdrop-blur-lg bg-white/80 rounded-2xl shadow-xl border border-white/20 p-8 mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success-100 rounded-full mb-6">
              <svg
                className="w-8 h-8 text-success-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Welcome, {firstName}!
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your account has been created successfully. We are excited to help you experience the
              healing benefits of professional massage therapy.
            </p>
          </div>

          {/* Getting Started Steps */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-8">
              Getting Started is Easy
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step) => (
                <div
                  key={step.number}
                  className="relative backdrop-blur-lg bg-white/80 rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-shadow"
                >
                  {/* Step Number */}
                  <div className="absolute -top-4 left-6 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="text-primary-600 mb-4 mt-2">{step.icon}</div>

                  {/* Content */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors shadow-lg w-full sm:w-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Browse Services
            </Link>
            <Link
              to="/profile"
              className="inline-flex items-center justify-center px-8 py-3 border border-primary-200 bg-white/50 text-gray-700 font-semibold rounded-xl hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors w-full sm:w-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Complete Profile
            </Link>
          </div>

          {/* Skip Link */}
          <div className="text-center mb-12">
            <Link
              to="/dashboard"
              className="text-sm text-gray-600 hover:text-primary-600 underline transition-colors"
            >
              Skip for now and go to dashboard
            </Link>
          </div>

          {/* Benefits Section - Glassmorphism Card */}
          <div className="backdrop-blur-lg bg-white/80 rounded-2xl shadow-xl border border-white/20 p-8">
            <h2 className="text-xl font-semibold text-gray-900 text-center mb-8">
              Why Choose Massatherapy?
            </h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: 'shield', text: 'Licensed Therapists' },
                { icon: 'clock', text: 'Flexible Scheduling' },
                { icon: 'credit-card', text: 'Secure Payments' },
                { icon: 'star', text: '5-Star Reviews' },
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 text-gray-600">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary-100/80 rounded-full flex items-center justify-center">
                    {benefit.icon === 'shield' && (
                      <svg
                        className="w-5 h-5 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    )}
                    {benefit.icon === 'clock' && (
                      <svg
                        className="w-5 h-5 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                    {benefit.icon === 'credit-card' && (
                      <svg
                        className="w-5 h-5 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                    )}
                    {benefit.icon === 'star' && (
                      <svg
                        className="w-5 h-5 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

WelcomePage.displayName = 'WelcomePage';

export default WelcomePage;
